import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Trash2, 
  Copy, 
  Download, 
  RefreshCw,
  AlertTriangle,
  XCircle,
  Shield,
  Users,
  FileText,
  Loader2,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { ReactComponent as ClusterIcon } from '../assets/icons/cluster.svg';
import {
  apiClient,
  ClusterSummary,
  GenerateOperatorTokenRequest,
  cleanErrorMessage
} from '../api/client';
import { getClusterCapabilities } from '../utils/clusterHealth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

// Operator deployment location options for on-prem
type OperatorDeploymentLocation = 'same-cluster' | 'same-vpc' | 'different-vpc' | 'custom';

type MetricsSource = 'none' | 'opentelemetry' | 'datadog';

interface TokenModalData {
  token: string;
  clusterId: string;
  clusterName: string;
  flowCollection: string;
  metricsSource: MetricsSource;
  datadogNamespace: string;
  argoCDEnabled: boolean;
  argoCDNamespace: string;
  safeApplyPermissions: boolean;
  chartVersion: string;
  chartName: string; // Chart name: kestrel-operator (SaaS) or kestrel-operator-onprem (on-prem)
  serverHost?: string; // On-prem only: where the operator connects to the server
  deploymentLocation?: OperatorDeploymentLocation; // On-prem only: where operator is deployed
  workloadCount?: number; // Number of workloads in cluster, used for resource sizing
  mtlsCertificates?: {
    clientCert: string;
    clientKey: string;
    caCert: string;
  };
}

// Resource sizing tiers based on workload count
function getResourcesForWorkloadCount(count: number): { limits: { cpu: string; memory: string }; requests: { cpu: string; memory: string } } {
  if (count <= 250) {
    return { limits: { cpu: '500m', memory: '2Gi' }, requests: { cpu: '250m', memory: '1Gi' } };
  } else if (count <= 1000) {
    return { limits: { cpu: '1000m', memory: '4Gi' }, requests: { cpu: '500m', memory: '2Gi' } };
  } else if (count <= 4000) {
    return { limits: { cpu: '2000m', memory: '8Gi' }, requests: { cpu: '1000m', memory: '4Gi' } };
  } else {
    return { limits: { cpu: '4000m', memory: '16Gi' }, requests: { cpu: '2000m', memory: '8Gi' } };
  }
}

// Common incident types that can be auto-remediated
const INCIDENT_TYPES = [
  { value: 'OOMKilled', label: 'OOMKilled (Out of Memory)', description: 'Pods killed due to memory limits' },
  { value: 'CrashLoopBackOff', label: 'CrashLoopBackOff', description: 'Pods repeatedly crashing' },
  { value: 'ImagePullError', label: 'ImagePullError', description: 'Unable to pull container images' },
  { value: 'ConfigError', label: 'ConfigError', description: 'Configuration or mount errors' },
  { value: 'VolumeError', label: 'VolumeError', description: 'Persistent volume issues' },
  { value: 'RolloutFailure', label: 'RolloutFailure', description: 'Deployment rollout failures' },
  { value: 'LivenessProbeFailure', label: 'LivenessProbeFailure', description: 'Liveness probe failures' },
  { value: 'ReadinessProbeFailure', label: 'ReadinessProbeFailure', description: 'Readiness probe failures' },
];

const GenerateOperatorToken: React.FC = () => {
  const [tokens, setTokens] = useState<ClusterSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [tokenModalData, setTokenModalData] = useState<TokenModalData | null>(null);
  const [formData, setFormData] = useState({
    cluster_name: '',
    description: '',
    flow_collection: 'cilium', // 'cilium' or 'istio'
    metrics_source: 'none' as MetricsSource, // 'none', 'opentelemetry', or 'datadog'
    datadog_namespace: 'datadog', // Namespace where Datadog is deployed
    argocd_enabled: false, // Whether ArgoCD is deployed in this cluster
    argocd_namespace: 'argocd', // Namespace where ArgoCD is deployed
    safe_apply: false, // Whether YAML changes can be auto-applied
    safe_apply_permissions: false, // Whether operator should have permissions to apply resources
    auto_remediate: false, // Whether incidents should be auto-remediated
    auto_remediate_incident_types: [] as string[], // Which incident types to auto-remediate
    workload_count: '', // Number of workloads for resource sizing
    // On-prem specific fields
    deployment_location: 'same-cluster' as OperatorDeploymentLocation,
    server_host: '' // Custom server host (only used when deployment_location is 'custom' or 'different-vpc')
  });
  const [errors, setErrors] = useState<{cluster_name?: string; description?: string}>({});
  const [modalError, setModalError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clusterToDelete, setClusterToDelete] = useState<{id: string, name: string} | null>(null);
  const [safeApplyDialogOpen, setSafeApplyDialogOpen] = useState(false);
  const [pendingSafeApplyChange, setPendingSafeApplyChange] = useState<{clusterId: string, safeApply: boolean} | null>(null);
  const [autoRemediationDialogOpen, setAutoRemediationDialogOpen] = useState(false);
  const [editingAutoRemediation, setEditingAutoRemediation] = useState<{
    clusterId: string;
    clusterName: string;
    safeApply: boolean;
    currentAutoRemediate: boolean;
    currentIncidentTypes: string[];
  } | null>(null);
  const [tempAutoRemediateSettings, setTempAutoRemediateSettings] = useState<{
    enabled: boolean;
    incidentTypes: string[];
  }>({ enabled: false, incidentTypes: [] });
  
  // On-prem mode detection
  const [isOnPremMode, setIsOnPremMode] = useState(false);

  // Dataplane region info (which Kestrel dataplane operators connect to)
  const [dataplaneRegion, setDataplaneRegion] = useState<string>('');
  const [dataplaneHost, setDataplaneHost] = useState<string>('');

  // Fetch on-prem status on mount
  useEffect(() => {
    const fetchOnPremStatus = async () => {
      try {
        const health = await apiClient.getHealthStatus();
        setIsOnPremMode(health.is_onprem_mode || false);
        setDataplaneRegion(health.dataplane_region || '');
        setDataplaneHost(health.dataplane_host || '');
      } catch (error) {
        console.error('Failed to fetch on-prem status:', error);
        setIsOnPremMode(false);
      }
    };
    fetchOnPremStatus();
  }, []);

  // Toasts go through the app-wide themed sonner Toaster (bottom-right) so
  // they never overlap the page header actions.
  const showToast = (type: 'success' | 'error', message: string) => {
    if (type === 'error') {
      toast.error(message);
    } else {
      toast.success(message);
    }
  };

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
              const response = await apiClient.listClusterSummaries();
      setTokens(response.clusters || []);
    } catch (error: any) {
      // Extract and clean error message
      const errorMessage = cleanErrorMessage(error) || 'Failed to fetch operator tokens';
      
      showToast('error', errorMessage);
      console.error('Error fetching tokens:', error);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const validateForm = () => {
    const newErrors: {cluster_name?: string; description?: string} = {};
    
    if (!formData.cluster_name.trim()) {
      newErrors.cluster_name = 'Cluster name is required';
    } else if (!/^[a-zA-Z0-9_.-]+$/.test(formData.cluster_name)) {
      newErrors.cluster_name = 'Only alphanumeric characters, dots, dashes, and underscores are allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous modal error
    setModalError('');
    
    if (!validateForm()) return;

    setCreatingToken(true);
    try {
      const request: GenerateOperatorTokenRequest = {
        cluster_name: formData.cluster_name,
        description: formData.description || '',
        safe_apply: formData.safe_apply,
      };
      
      const response = await apiClient.generateOperatorToken(request);
      
      // Determine server host based on deployment location (on-prem only)
      let serverHost: string | undefined;
      if (isOnPremMode) {
        switch (formData.deployment_location) {
          case 'same-cluster':
            serverHost = 'kestrel-autonp-server-grpc.kestrel.svc.cluster.local';
            break;
          case 'same-vpc':
            // User should get the NLB DNS after deployment
            serverHost = formData.server_host || '';
            break;
          case 'different-vpc':
          case 'custom':
            serverHost = formData.server_host || '';
            break;
        }
      }
      
      setTokenModalData({
        token: response.access_token,
        clusterId: response.cluster_id,
        clusterName: response.cluster_name,
        flowCollection: formData.flow_collection,
        metricsSource: formData.metrics_source,
        datadogNamespace: formData.datadog_namespace,
        argoCDEnabled: formData.argocd_enabled,
        argoCDNamespace: formData.argocd_namespace,
        safeApplyPermissions: formData.safe_apply_permissions,
        chartVersion: response.chart_version || 'latest',
        chartName: response.chart_name || 'kestrel-operator', // SaaS default, on-prem returns kestrel-operator-onprem
        serverHost: serverHost,
        deploymentLocation: isOnPremMode ? formData.deployment_location : undefined,
        workloadCount: formData.workload_count ? parseInt(formData.workload_count, 10) : undefined,
        mtlsCertificates: response.mtls_certificates ? {
          clientCert: response.mtls_certificates.client_cert,
          clientKey: response.mtls_certificates.client_key,
          caCert: response.mtls_certificates.ca_cert,
        } : undefined,
      });
      
      // Clear form and close modal on success
      setFormData({ cluster_name: '', description: '', flow_collection: 'cilium', metrics_source: 'none' as MetricsSource, datadog_namespace: 'datadog', argocd_enabled: false, argocd_namespace: 'argocd', safe_apply: false, safe_apply_permissions: false, auto_remediate: false, auto_remediate_incident_types: [], workload_count: '', deployment_location: 'same-cluster', server_host: '' });
      setModalError('');
      setModalVisible(false);
      setTokenModalVisible(true);
      fetchTokens();
      showToast('success', 'Operator token generated successfully!');
    } catch (error: any) {
      // Extract and clean error message
      let errorMessage = cleanErrorMessage(error) || 'Failed to generate operator token';
      
      // Handle subscription-specific error types
      if (errorMessage.includes('no active subscription')) {
        errorMessage = `${errorMessage.trim()}\n\nPlease visit the Billing page to subscribe to a plan.`;
      } else if (errorMessage.includes('subscription is not active')) {
        errorMessage = `${errorMessage.trim()}\n\nPlease update your payment method in the billing portal or contact hello@usekestrel.ai`;
      } else if (errorMessage.includes('cluster limit reached') || errorMessage.includes('tier allows maximum')) {
        errorMessage = `${errorMessage.trim()}\n\nPlease visit the Billing page to upgrade your plan or contact hello@usekestrel.ai`;
      } else if (errorMessage.includes('subscription cluster limit reached')) {
        errorMessage = `${errorMessage.trim()}\n\nPlease visit the Billing page to increase your cluster quantity or upgrade your tier.`;
      }
      
      // Show error in the modal instead of closing it
      setModalError(errorMessage);
      console.error('Error generating token:', error);
    } finally {
      setCreatingToken(false);
    }
  };

  const handleDeleteToken = (clusterId: string, clusterName: string) => {
    setClusterToDelete({ id: clusterId, name: clusterName });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteToken = async () => {
    if (!clusterToDelete) return;

    try {
      await apiClient.deleteOperatorToken({ cluster_id: clusterToDelete.id });
      showToast('success', 'Operator token deleted successfully');
      fetchTokens();
      setDeleteDialogOpen(false);
      setClusterToDelete(null);
    } catch (error: any) {
      // Extract and clean error message
      const errorMessage = cleanErrorMessage(error) || 'Failed to delete operator token';
      
      showToast('error', errorMessage);
      console.error('Error deleting token:', error);
    }
  };

  const handleUpdateSafeApply = async (clusterId: string, safeApply: boolean) => {
    // Show confirmation dialog first
    setPendingSafeApplyChange({ clusterId, safeApply });
    setSafeApplyDialogOpen(true);
  };

  const confirmSafeApplyChange = async () => {
    if (!pendingSafeApplyChange) return;
    
    const { clusterId, safeApply } = pendingSafeApplyChange;
    
    try {
      await apiClient.updateClusterSafeApply({ cluster_id: clusterId, safe_apply: safeApply });
      showToast('success', `Safe-apply ${safeApply ? 'enabled' : 'disabled'} successfully`);
      // Update the local state immediately for better UX
      setTokens(prev => prev.map(token => 
        token.cluster_id === clusterId 
          ? { ...token, safe_apply: safeApply }
          : token
      ));
    } catch (error: any) {
      const errorMessage = cleanErrorMessage(error) || 'Failed to update safe-apply setting';
      showToast('error', errorMessage);
      console.error('Error updating safe-apply:', error);
      // Revert the switch state
      fetchTokens();
    } finally {
      setSafeApplyDialogOpen(false);
      setPendingSafeApplyChange(null);
    }
  };

  const handleEditAutoRemediation = (token: ClusterSummary) => {
    setEditingAutoRemediation({
      clusterId: token.cluster_id,
      clusterName: token.cluster_name,
      safeApply: token.safe_apply || false,
      currentAutoRemediate: token.auto_remediate || false,
      currentIncidentTypes: token.auto_remediate_incident_types || []
    });
    setTempAutoRemediateSettings({
      enabled: token.auto_remediate || false,
      incidentTypes: token.auto_remediate_incident_types || []
    });
    setAutoRemediationDialogOpen(true);
  };

  const saveAutoRemediationSettings = async () => {
    if (!editingAutoRemediation) return;

    try {
      await apiClient.updateClusterAutoRemediation({
        cluster_id: editingAutoRemediation.clusterId,
        auto_remediate: tempAutoRemediateSettings.enabled,
        incident_types: tempAutoRemediateSettings.incidentTypes
      });

      showToast('success', 'Auto-remediation settings updated successfully');

      // Update local state
      setTokens(prev => prev.map(token => 
        token.cluster_id === editingAutoRemediation.clusterId
          ? { 
              ...token, 
              auto_remediate: tempAutoRemediateSettings.enabled,
              auto_remediate_incident_types: tempAutoRemediateSettings.incidentTypes
            }
          : token
      ));

    } catch (error: any) {
      const errorMessage = cleanErrorMessage(error) || 'Failed to update auto-remediation settings';
      showToast('error', errorMessage);
      console.error('Error updating auto-remediation:', error);
    } finally {
      setAutoRemediationDialogOpen(false);
      setEditingAutoRemediation(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('success', 'Copied to clipboard!');
    });
  };



  const downloadYamlFile = () => {
    if (!tokenModalData) return;

    // Base configuration
    let content = `# Helm values for Kestrel AI Operator
# Generated on: ${new Date().toISOString()}
`;

    // Add server configuration for on-prem deployments
    if (tokenModalData.serverHost) {
      content += `
# ========================================
# ON-PREM SERVER CONFIGURATION
# ========================================
# This operator is configured for on-prem deployment
# Server host: ${tokenModalData.serverHost}
# TLS: Encryption enabled, server certificate verification disabled (on-prem mode)

server:
  host: "${tokenModalData.serverHost}"
  port: 443
  tls:
    insecureSkipVerify: true
`;
    }

    content += `
auth:
  token: "${tokenModalData.token}"

operator:
  cluster:
    id: "${tokenModalData.clusterId}"
    name: "${tokenModalData.clusterName}"`;

    // Add flow collection specific configuration
    if (tokenModalData.flowCollection === 'istio') {
      content += `
  cilium:
    disableFlows: true
  istio:
    enabled: true`;
    } else {
      // Cilium is the default, but we can be explicit
      content += `
  cilium:
    disableFlows: false
  istio:
    enabled: false`;
    }

    // Add safe-apply permissions configuration
    content += `
  safeApply:
    enabled: ${tokenModalData.safeApplyPermissions}`;

    // Add metrics source configuration
    if (tokenModalData.metricsSource === 'opentelemetry') {
      content += `
  otel:
    enabled: true
    receiverPort: 4317`;
    } else if (tokenModalData.metricsSource === 'datadog') {
      content += `
  datadog:
    enabled: true
    namespace: "${tokenModalData.datadogNamespace}"`;
    }

    if (tokenModalData.argoCDEnabled) {
      content += `
  argocd:
    enabled: true
    namespace: "${tokenModalData.argoCDNamespace}"`;
    }

    // Add resource sizing based on workload count
    if (tokenModalData.workloadCount && tokenModalData.workloadCount > 0) {
      const resources = getResourcesForWorkloadCount(tokenModalData.workloadCount);
      content += `

resources:
  limits:
    cpu: ${resources.limits.cpu}
    memory: ${resources.limits.memory}
  requests:
    cpu: ${resources.requests.cpu}
    memory: ${resources.requests.memory}`;
    }

    // Add mTLS certificates if available
    if (tokenModalData.mtlsCertificates) {
      content += `

# ========================================
# mTLS CERTIFICATES
# ========================================
# These certificates are unique to this operator deployment
# Generated by Kestrel Cloud for secure authentication
# Valid for 1 year from generation date (automatically renewed by Kestrel Cloud)

certificates:
  client:
    cert: |
${tokenModalData.mtlsCertificates.clientCert.split('\n').map(line => '      ' + line).join('\n')}
    key: |
${tokenModalData.mtlsCertificates.clientKey.split('\n').map(line => '      ' + line).join('\n')}
  ca:
    cert: |
${tokenModalData.mtlsCertificates.caCert.split('\n').map(line => '      ' + line).join('\n')}`;
    } else {
      content += `

# ========================================
# mTLS CERTIFICATES
# ========================================
# Note: mTLS certificates should be generated by the server
# If you see this message, certificate generation may have failed
# Contact support@usekestrel.ai if mTLS is required for your deployment`;
    }

    content += '\n';

    const blob = new Blob([content], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kestrel-ai-operator-values-${tokenModalData.clusterName}.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showToast('success', 'Values file downloaded!');
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp === 0) {
      return "Never";
    }
    const date = new Date(timestamp * 1000);
    // Use a consistent format: MM/DD/YYYY
    return isNaN(date.getTime()) ? "Never" : date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getConnectionStatus = (record: ClusterSummary) => {
    if (record.connection_status === 'degraded') {
      return {
        variant: 'default' as const,
        text: 'Degraded',
        description: 'Some streams are disconnected',
        className: 'bg-amber-500 hover:bg-amber-500/80 text-white border-amber-500'
      };
    } else if (record.is_connected) {
      return {
        variant: 'default' as const,
        text: 'Connected',
        description: 'Cluster is actively sending data',
        className: 'bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/30 font-normal'
      };
    } else if (record.last_seen > 0) {
      const lastSeenDate = new Date(record.last_seen * 1000);
      const timeAgo = formatTimeAgo(lastSeenDate);
      return {
        variant: 'secondary' as const,
        text: 'Disconnected',
        description: `Last seen ${timeAgo}`,
        className: 'bg-zinc-100 text-zinc-700 hover:bg-zinc-100 dark:bg-zinc-900/30 dark:text-zinc-400 dark:hover:bg-zinc-900/30 font-normal'
      };
    } else {
      return {
        variant: 'destructive' as const,
        text: 'Offline',
        description: 'Cluster has never connected with this token',
        className: 'bg-red-500 hover:bg-red-500/80 text-white border-red-500'
      };
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  // Simplified empty state component
  const EmptyState = () => (
    <div className="text-center py-12">
      <h3 className="text-lg font-medium mb-2">No cluster credentials found</h3>
      <p className="text-muted-foreground mb-4">Get started by generating your first cluster credential.</p>
      <Button onClick={() => setModalVisible(true)}>
        <Plus className="h-4 w-4 mr-2" />
        Generate Your First Credential
      </Button>
    </div>
  );

  // Simplified loading state
  const LoadingState = () => (
    <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={fetchTokens} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Dialog open={modalVisible} onOpenChange={(open) => {
            setModalVisible(open);
            if (open) {
              // Clear errors when opening modal
              setModalError('');
              setErrors({});
            } else {
              // Clear form data when closing modal
              setFormData({ cluster_name: '', description: '', flow_collection: 'cilium', metrics_source: 'none' as MetricsSource, datadog_namespace: 'datadog', argocd_enabled: false, argocd_namespace: 'argocd', safe_apply: false, safe_apply_permissions: false, auto_remediate: false, auto_remediate_incident_types: [], workload_count: '', deployment_location: 'same-cluster', server_host: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Generate New Credential
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Generate New Kestrel Operator Credential</DialogTitle>
                <DialogDescription>
                  Create a new credential to allow a cluster to connect to Kestrel AI.
                </DialogDescription>
              </DialogHeader>
              
              {/* Error Display */}
              {modalError && (
                <Alert variant="destructive" className="mb-4">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription className="whitespace-pre-wrap">
                    {modalError}
                    {(modalError.includes('Billing page') || modalError.includes('billing portal')) && (
                      <div className="mt-3">
                        <a
                          href="/billing"
                          className="inline-flex items-center gap-2 text-sm font-medium underline hover:no-underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModalVisible(false);
                            setModalError('');
                          }}
                        >
                          Go to Billing Page →
                        </a>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={handleCreateToken} className="space-y-4">
                <FormItem>
                  <FormLabel htmlFor="cluster_name">Cluster Name</FormLabel>
                  <Input
                    id="cluster_name"
                    value={formData.cluster_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, cluster_name: e.target.value }))}
                    placeholder="e.g., production-cluster"
                    className={errors.cluster_name ? "border-destructive" : ""}
                  />
                  {errors.cluster_name && (
                    <FormMessage>{errors.cluster_name}</FormMessage>
                  )}
                </FormItem>
                
                <FormItem>
                  <FormLabel htmlFor="description">Description (Optional)</FormLabel>
                  <Textarea 
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe this cluster or its purpose..."
                    rows={3}
                    className="resize-none"
                  />
                </FormItem>

                <FormItem>
                  <FormLabel htmlFor="flow_collection">Flow Collection Source</FormLabel>
                  <Select
                    value={formData.flow_collection}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, flow_collection: value }))}
                  >
                    <SelectTrigger className="w-full py-6 px-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cilium">
                        <div className="flex flex-col items-start text-left">
                          <span className="font-medium">Cilium (Hubble Relay)</span>
                          <span className="text-xs text-muted-foreground">Collect L3/L4 network flows from Cilium CNI</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="istio">
                        <div className="flex flex-col items-start text-left">
                          <span className="font-medium">Istio (Envoy Proxy)</span>
                          <span className="text-xs text-muted-foreground">Collect L7 access logs from Istio service mesh</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="aws-vpc-cni">
                        <div className="flex flex-col items-start text-left">
                          <span className="font-medium">AWS EKS VPC CNI</span>
                          <span className="text-xs text-muted-foreground">Collect VPC flow logs from AWS EKS clusters</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>

                <FormItem>
                  <FormLabel htmlFor="metrics_source">Metrics Source</FormLabel>
                  <Select
                    value={formData.metrics_source}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, metrics_source: value as MetricsSource }))}
                  >
                    <SelectTrigger className="w-full py-6 px-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex flex-col items-start text-left">
                          <span className="font-medium">Kubernetes Metrics Server</span>
                          <span className="text-xs text-muted-foreground">Uses the built-in K8s Metrics API if available</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="opentelemetry">
                        <div className="flex flex-col items-start text-left">
                          <span className="font-medium">OpenTelemetry</span>
                          <span className="text-xs text-muted-foreground">Receive metrics from an OTEL Collector via OTLP gRPC</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="datadog">
                        <div className="flex flex-col items-start text-left">
                          <span className="font-medium">Datadog</span>
                          <span className="text-xs text-muted-foreground">Query metrics from in-cluster Datadog agents</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.metrics_source === 'datadog' && (
                    <div className="mt-2 space-y-2">
                      <div>
                        <label htmlFor="datadog_namespace" className="text-xs font-medium text-foreground">Datadog Namespace</label>
                        <Input
                          id="datadog_namespace"
                          placeholder="datadog"
                          value={formData.datadog_namespace}
                          onChange={(e) => setFormData(prev => ({ ...prev, datadog_namespace: e.target.value }))}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          The namespace where Datadog is deployed. The Kestrel Operator will auto-discover Datadog. No manual key configuration needed.
                        </p>
                      </div>
                    </div>
                  )}
                  {formData.metrics_source === 'opentelemetry' && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Configure your OTEL Collector to send metrics to the operator's OTLP gRPC endpoint (port 4317).
                    </p>
                  )}
                </FormItem>

                {/* ArgoCD Integration */}
                <FormItem>
                  <div className="flex items-center justify-between">
                    <div>
                      <FormLabel>ArgoCD</FormLabel>
                      <p className="text-sm text-muted-foreground">Enable if ArgoCD is deployed in this cluster for GitOps deployment sync.</p>
                    </div>
                    <Switch
                      checked={formData.argocd_enabled}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, argocd_enabled: checked }))}
                    />
                  </div>
                  {formData.argocd_enabled && (
                    <div className="mt-2 space-y-2">
                      <div>
                        <label htmlFor="argocd_namespace" className="text-xs font-medium text-foreground">ArgoCD Namespace</label>
                        <Input
                          id="argocd_namespace"
                          placeholder="argocd"
                          value={formData.argocd_namespace}
                          onChange={(e) => setFormData(prev => ({ ...prev, argocd_namespace: e.target.value }))}
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          The namespace where ArgoCD is deployed. The Kestrel Operator will auto-discover the ArgoCD server and authenticate using the in-cluster admin secret — no manual URL or token needed.
                        </p>
                      </div>
                    </div>
                  )}
                </FormItem>

                {/* Workload Count for Resource Sizing */}
                <FormItem>
                  <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
                    <div>
                      <FormLabel className="font-medium">Cluster Workload Count</FormLabel>
                      <p className="text-sm text-muted-foreground mt-1">
                        Determines CPU and memory allocated to the Kestrel Operator. Run this command on your cluster to get the count:
                      </p>
                    </div>
                    <div className="relative">
                      <code className="block bg-muted px-3 py-2 pr-10 rounded text-xs font-mono select-all overflow-x-auto">
                        kubectl get deploy,sts,ds,job,cj -A --no-headers | wc -l
                      </code>
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          navigator.clipboard.writeText('kubectl get deploy,statefulset,daemonset,job,cronjob -A --no-headers | wc -l');
                          showToast('success', 'Command copied to clipboard!');
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      <Input
                        id="workload_count"
                        type="number"
                        min="1"
                        step="1"
                        value={formData.workload_count}
                        onKeyDown={(e) => {
                          // Block e, E, +, -, . to only allow whole positive numbers
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Only accept empty string or positive integers
                          if (raw === '' || /^\d+$/.test(raw)) {
                            setFormData(prev => ({ ...prev, workload_count: raw }));
                          }
                        }}
                        placeholder="e.g., 150"
                      />
                      {formData.workload_count && /^\d+$/.test(formData.workload_count) && Number(formData.workload_count) > 0 && (() => {
                        const count = Number(formData.workload_count);
                        const resources = getResourcesForWorkloadCount(count);
                        const tier = count <= 250 ? '≤ 250' : count <= 1000 ? '≤ 1,000' : count <= 4000 ? '≤ 4,000' : '> 4,000';
                        return (
                          <p className="text-xs text-muted-foreground mt-1">
                            Tier: <strong>{tier} workloads</strong> — CPU: {resources.requests.cpu}–{resources.limits.cpu}, Memory: {resources.requests.memory}–{resources.limits.memory}
                          </p>
                        );
                      })()}
                      {!formData.workload_count && (
                        <p className="text-xs text-muted-foreground">
                          Optional. Defaults to CPU and memory suitable for up to 250 workloads.
                        </p>
                      )}
                    </div>
                  </div>
                </FormItem>

                {/* On-Prem Server Connectivity Section */}
                {isOnPremMode && (
                  <FormItem>
                    <div className="p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-950/20 space-y-4">
                      <div>
                        <FormLabel className="font-medium">Server Connectivity</FormLabel>
                        <p className="text-sm text-muted-foreground mt-1">
                          Where will this Kestrel Operator be deployed relative to the Kestrel server?
                        </p>
                      </div>
                      
                      <Select
                        value={formData.deployment_location}
                        onValueChange={(value: OperatorDeploymentLocation) => setFormData(prev => ({ 
                          ...prev, 
                          deployment_location: value,
                          // Clear custom host when switching away from custom/different-vpc
                          server_host: value === 'same-cluster' ? '' : prev.server_host
                        }))}
                      >
                        <SelectTrigger className="w-full h-auto py-3">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="same-cluster">
                            <div className="flex flex-col items-start text-left">
                              <span className="font-medium">Same Kubernetes Cluster</span>
                              <span className="text-xs text-muted-foreground">Kestrel Operator is deployed in the same cluster as Kestrel server</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="same-vpc">
                            <div className="flex flex-col items-start text-left">
                              <span className="font-medium">Same VPC, Different Cluster</span>
                              <span className="text-xs text-muted-foreground">Kestrel Operator can reach server via internal load balancer</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="different-vpc">
                            <div className="flex flex-col items-start text-left">
                              <span className="font-medium">Different VPC</span>
                              <span className="text-xs text-muted-foreground">Requires VPC Peering, Transit Gateway, or PrivateLink</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="custom">
                            <div className="flex flex-col items-start text-left">
                              <span className="font-medium">Custom DNS / Air-Gapped</span>
                              <span className="text-xs text-muted-foreground">Using custom internal DNS (e.g., Route 53 Private Hosted Zone)</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Instructions based on deployment location */}
                      <div className="text-xs text-muted-foreground bg-background/50 p-3 rounded border space-y-2">
                        {formData.deployment_location === 'same-cluster' && (
                          <>
                            <p><strong>Configuration:</strong> Server host will be set to the internal Kubernetes service DNS:</p>
                            <code className="block bg-muted px-2 py-1 rounded text-xs">kestrel-autonp-server-grpc.kestrel.svc.cluster.local</code>
                            <p className="text-green-600 dark:text-green-400">No additional setup required.</p>
                          </>
                        )}
                        {formData.deployment_location === 'same-vpc' && (
                          <>
                            <p><strong>How to get the server host:</strong></p>
                            <ol className="list-decimal list-inside space-y-1">
                              <li>After deploying Kestrel, run: <code className="bg-muted px-1 rounded">kubectl get svc -n kestrel | grep grpc</code></li>
                              <li>Copy the EXTERNAL-IP (e.g., <code className="bg-muted px-1 rounded">a1b2c3...elb.us-west-1.amazonaws.com</code>)</li>
                              <li>Enter it below (without the port)</li>
                            </ol>
                            <p className="text-muted-foreground text-xs mt-2">
                              Note: Connection is encrypted (TLS). Server certificate verification is disabled for on-prem flexibility.
                            </p>
                          </>
                        )}
                        {formData.deployment_location === 'different-vpc' && (
                          <>
                            <p><strong>Step 1:</strong> Set up network connectivity:</p>
                            <ul className="list-disc list-inside space-y-1">
                              <li><strong>VPC Peering:</strong> VPC → Peering Connections → Create, accept request, update route tables</li>
                              <li><strong>Transit Gateway:</strong> VPC → Transit Gateways → Create, attach VPCs, update route tables</li>
                              <li><strong>PrivateLink:</strong> Create VPC Endpoint Service from the gRPC NLB</li>
                            </ul>
                            <p className="mt-2"><strong>Step 2:</strong> Enter the server hostname (NLB DNS or VPC Endpoint DNS) below.</p>
                            <p className="text-muted-foreground text-xs mt-2">
                              Note: Connection is encrypted (TLS). Server certificate verification is disabled for on-prem flexibility.
                            </p>
                          </>
                        )}
                        {formData.deployment_location === 'custom' && (
                          <>
                            <p><strong>For air-gapped environments with custom DNS:</strong></p>
                            <ol className="list-decimal list-inside space-y-1">
                              <li>Create a Route 53 Private Hosted Zone</li>
                              <li>Add an A record pointing to the gRPC NLB</li>
                              <li>Associate all VPCs with the hosted zone</li>
                              <li>Enter your custom hostname below (e.g., <code className="bg-muted px-1 rounded">kestrel-grpc.internal.company.com</code>)</li>
                            </ol>
                            <p className="text-muted-foreground text-xs mt-2">
                              Note: Connection is encrypted (TLS). Server certificate verification is disabled for on-prem flexibility.
                            </p>
                          </>
                        )}
                      </div>
                      
                      {/* Server Host Input - Show for same-vpc, different-vpc, and custom */}
                      {formData.deployment_location !== 'same-cluster' && (
                        <div className="space-y-2">
                          <FormLabel htmlFor="server_host">Server Host</FormLabel>
                          <Input
                            id="server_host"
                            value={formData.server_host}
                            onChange={(e) => setFormData(prev => ({ ...prev, server_host: e.target.value }))}
                            placeholder={
                              formData.deployment_location === 'same-vpc' 
                                ? "internal-xxx.elb.us-west-1.amazonaws.com"
                                : formData.deployment_location === 'different-vpc'
                                ? "vpce-xxx.vpce-svc-xxx.us-west-1.vpce.amazonaws.com"
                                : "kestrel-grpc.internal.company.com"
                            }
                          />
                          <p className="text-xs text-muted-foreground">
                            Enter the hostname only (without protocol or port). Port 443 will be used automatically.
                          </p>
                        </div>
                      )}
                    </div>
                  </FormItem>
                )}

                <FormItem>
                  <div className="flex items-center space-x-3 p-4 border rounded-lg">
                    <Switch
                      id="safe_apply_permissions"
                      checked={formData.safe_apply_permissions}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, safe_apply_permissions: checked }))}
                    />
                    <div className="flex-1">
                      <FormLabel htmlFor="safe_apply_permissions" className="font-medium">
                        Grant Safe-Apply Permissions to Kestrel Operator
                      </FormLabel>
                      <p className="text-sm text-muted-foreground mt-1">
                        Allow the operator to create, update, and delete Kubernetes resources. Required for safe-apply functionality (approved YAML configuration changes can be applied directly to this cluster).
                      </p>
                    </div>
                  </div>
                </FormItem>

                {/* Auto-Remediation Section - Only show if safe_apply is enabled */}
                {formData.safe_apply && (
                  <FormItem>
                    <div className="p-4 border-2 border-dashed border-muted-foreground/30 rounded-lg bg-muted/30">
                      <div className="flex items-start space-x-3 mb-3">
                        <Switch
                          id="auto_remediate"
                          checked={formData.auto_remediate}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({ 
                              ...prev, 
                              auto_remediate: checked,
                              auto_remediate_incident_types: checked ? prev.auto_remediate_incident_types : []
                            }))
                          }}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <FormLabel htmlFor="auto_remediate" className="font-medium">
                              Enable Auto-Remediation for Incidents
                            </FormLabel>
                            <Badge variant="secondary" className="text-xs">BETA</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            AI-generated fixes for selected incident types will be <strong>automatically applied</strong> to your cluster.
                          </p>
                          <Alert className="mt-3 bg-muted/50 border">
                            <AlertDescription className="text-xs">
                              <strong>Warning:</strong> AI fixes will be applied directly to your cluster without manual approval. Only enable for non-production clusters or incident types you fully trust.
                            </AlertDescription>
                          </Alert>
                        </div>
                      </div>

                      {/* Incident Type Selection */}
                      {formData.auto_remediate && (
                        <div className="mt-4 pl-11">
                          <FormLabel className="text-sm font-medium mb-2 block">
                            Select Incident Types to Auto-Remediate
                          </FormLabel>
                          <p className="text-xs text-muted-foreground mb-3">
                            Choose which incident types should be automatically remediated. Only non-transient incidents will be auto-fixed.
                          </p>
                          <div className="space-y-2 max-h-64 overflow-y-auto p-3 bg-background rounded-md border">
                            {INCIDENT_TYPES.map((incidentType) => (
                              <div key={incidentType.value} className="flex items-start space-x-3 p-2 hover:bg-muted/50 rounded">
                                <input
                                  type="checkbox"
                                  id={`incident-type-${incidentType.value}`}
                                  checked={formData.auto_remediate_incident_types.includes(incidentType.value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData(prev => ({
                                        ...prev,
                                        auto_remediate_incident_types: [...prev.auto_remediate_incident_types, incidentType.value]
                                      }));
                                    } else {
                                      setFormData(prev => ({
                                        ...prev,
                                        auto_remediate_incident_types: prev.auto_remediate_incident_types.filter(t => t !== incidentType.value)
                                      }));
                                    }
                                  }}
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <label htmlFor={`incident-type-${incidentType.value}`} className="flex-1 cursor-pointer">
                                  <div className="font-medium text-sm">{incidentType.label}</div>
                                  <div className="text-xs text-muted-foreground">{incidentType.description}</div>
                                </label>
                              </div>
                            ))}
                          </div>
                          {formData.auto_remediate_incident_types.length === 0 && (
                            <p className="text-xs text-muted-foreground mt-2 italic">
                              No incident types selected. Auto-remediation will not trigger until you select at least one type.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </FormItem>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" disabled={creatingToken} onClick={() => {
                    setModalVisible(false);
                    setModalError('');
                    setFormData({ cluster_name: '', description: '', flow_collection: 'cilium', metrics_source: 'none' as MetricsSource, datadog_namespace: 'datadog', argocd_enabled: false, argocd_namespace: 'argocd', safe_apply: false, safe_apply_permissions: false, auto_remediate: false, auto_remediate_incident_types: [], workload_count: '', deployment_location: 'same-cluster', server_host: '' });
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creatingToken}>
                    {creatingToken && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                    {creatingToken ? 'Generating...' : 'Generate Credential'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Main Content */}
        <Card>
          <CardContent className="p-6">
            {tokens.length === 0 ? (
              <EmptyState />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cluster Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Safe-Apply</TableHead>
                    <TableHead>Auto-Remediation</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((token) => {
                    const statusInfo = getConnectionStatus(token);
                    return (
                      <TableRow key={token.cluster_id}>
                        <TableCell className="font-medium max-w-32 truncate">{token.cluster_name}</TableCell>
                        <TableCell className="max-w-48">
                          <div className="truncate">
                            {token.description || <span className="text-muted-foreground">No description</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant={statusInfo.variant}
                                className={`cursor-help px-3 py-1 font-medium text-xs min-w-[80px] justify-center ${statusInfo.className}`}
                              >
                                {statusInfo.text}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs">
                              <p className="font-medium mb-1">{statusInfo.description}</p>
                              {(statusInfo.text === 'Connected' || statusInfo.text === 'Degraded' || statusInfo.text === 'Disconnected') && (
                                <ul className="space-y-0.5 text-xs">
                                  {getClusterCapabilities(token.stream_health, token.connection_status).map((cap) => (
                                    <li key={cap.label} className="flex items-center gap-1.5">
                                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${cap.online ? 'bg-green-400' : 'bg-red-400'}`} />
                                      {cap.label}: {cap.online ? 'Online' : 'Offline'}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {dataplaneRegion && (statusInfo.text === 'Connected' || statusInfo.text === 'Degraded' || statusInfo.text === 'Disconnected') && (
                                <p className="mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground">
                                  Connected to <span className="font-medium text-foreground">{dataplaneRegion}</span> dataplane{dataplaneHost ? ` (${dataplaneHost})` : ''}
                                </p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={token.safe_apply || false}
                            onCheckedChange={(checked) => handleUpdateSafeApply(token.cluster_id, checked)}
                            disabled={loading}
                            className="data-[state=checked]:bg-primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAutoRemediation(token)}
                            disabled={!token.safe_apply}
                          >
                            {token.auto_remediate ? (
                              <>
                                On
                                {token.auto_remediate_incident_types && token.auto_remediate_incident_types.length > 0 && (
                                  <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                                    {token.auto_remediate_incident_types.length}
                                  </Badge>
                                )}
                              </>
                            ) : 'Off'}
                          </Button>
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(token.created_at)}</TableCell>
                        <TableCell className="text-sm">
                          <span className={formatDate(token.last_seen) === "Never" ? "text-muted-foreground" : ""}>
                            {formatDate(token.last_seen)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteToken(token.cluster_id, token.cluster_name)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Token Display Modal */}
        <Dialog open={tokenModalVisible} onOpenChange={setTokenModalVisible}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0 pb-4">
              <DialogTitle className="text-xl">Kestrel Operator Credential Generated</DialogTitle>
              <DialogDescription className="text-base">
                Your new operator credential has been generated successfully.
              </DialogDescription>
            </DialogHeader>
            
            {tokenModalData && (
              <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                {/* Cluster Info */}
                <div className="p-4 bg-muted/50 border border-border rounded-lg">
                  <div className="flex items-center gap-2 text-base font-semibold text-foreground">
                    <ClusterIcon className="h-5 w-5" />
                    Cluster: {tokenModalData.clusterName}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">ID: {tokenModalData.clusterId}</div>
                </div>
                
                {/* Download Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-base">1. Download Helm Values</h4>
                    <Button 
                      onClick={downloadYamlFile} 
                      size="sm"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>

                {/* Step 2: Navigate to download directory */}
                <div className="space-y-3">
                  <h4 className="font-medium text-base">2. Navigate to the Downloaded File</h4>
                  <p className="text-sm text-muted-foreground">
                    Open a terminal and navigate to the directory where the Helm values file was downloaded, or move it to your working directory.
                  </p>
                  <div className="bg-muted/50 p-3 rounded border font-mono text-xs">
                    cd ~/Downloads
                  </div>
                </div>

                {/* Step 3: Run Helm install */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-base">3. Deploy the Operator</h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => copyToClipboard(`helm install kestrel-operator oci://ghcr.io/kestrelai/charts/${tokenModalData.chartName} --version ${tokenModalData.chartVersion} --namespace kestrel-ai --create-namespace -f kestrel-ai-operator-values-${tokenModalData.clusterName}.yaml`)}
                      className="border-border hover:bg-muted"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Command
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Run the following Helm command to install the Kestrel Operator in your Kubernetes cluster:
                  </p>
                  <div className="bg-muted/50 p-3 rounded border font-mono text-xs">
                    helm install kestrel-operator oci://ghcr.io/kestrelai/charts/{tokenModalData.chartName} --version {tokenModalData.chartVersion} --namespace kestrel-ai --create-namespace -f kestrel-ai-operator-values-{tokenModalData.clusterName}.yaml
                  </div>
                </div>

                {/* Step 4: Verify */}
                <div className="space-y-3">
                  <h4 className="font-medium text-base">4. Verify the Operator is Running</h4>
                  <div className="bg-muted/50 p-3 rounded border font-mono text-xs">
                    kubectl get pods -l app=kestrel-operator -n kestrel-ai
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-muted/30 border border-border p-4 rounded-md">
                  <div className="text-xs space-y-1 leading-relaxed">
                    <p className="text-muted-foreground"><strong>Flow Collection:</strong> This operator is configured for {tokenModalData.flowCollection === 'istio' ? 'Istio (L7 access logs)' : tokenModalData.flowCollection === 'aws-vpc-cni' ? 'AWS EKS VPC CNI (VPC flow logs)' : 'Cilium (L3/L4 flows)'}.</p>
                    {tokenModalData.metricsSource === 'datadog' && (
                      <p className="text-blue-600 dark:text-blue-400"><strong>Metrics:</strong> Datadog integration enabled. The operator will read the API key from the Datadog secret in your cluster — no manual key configuration required.</p>
                    )}
                    {tokenModalData.metricsSource === 'opentelemetry' && (
                      <p className="text-blue-600 dark:text-blue-400"><strong>Metrics:</strong> OpenTelemetry enabled. Point your OTEL Collector's OTLP exporter to <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 rounded">kestrel-operator.kestrel-ai:4317</code>.</p>
                    )}
                    {tokenModalData.argoCDEnabled && (
                      <p className="text-blue-600 dark:text-blue-400"><strong>ArgoCD:</strong> ArgoCD integration enabled. The operator will auto-discover the ArgoCD server and authenticate using the in-cluster admin secret.</p>
                    )}
                    <p className="text-muted-foreground"><strong>Token Lifecycle:</strong> The operator automatically renews this token every 24 hours, so your cluster will remain connected.</p>
                  </div>
                </div>

                {/* AWS VPC CNI - connect VPC flow logs reminder */}
                {tokenModalData.flowCollection === 'aws-vpc-cni' && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      <strong>VPC Flow Logs Required:</strong> To collect network flow data from your EKS cluster, ensure VPC Flow Logs are enabled for the VPC where your cluster runs. You can connect your AWS account and configure VPC flow logs in{' '}
                      <a href="/cloud-integrations" className="font-medium underline hover:no-underline">
                        Integrations &rarr; Cloud
                      </a>.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Air-Gapped Deployment Section - Only shown when Custom DNS / Air-Gapped is selected */}
                {tokenModalData.deploymentLocation === 'custom' && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-base">Air-Gapped Deployment</h4>
                    <div className="bg-muted/50 border p-4 rounded-md">
                      <div className="text-xs space-y-3 leading-relaxed">
                        <div className="space-y-2">
                          <p><strong>Step 1: Download the Helm chart</strong></p>
                          <p className="text-muted-foreground">From a machine with internet access, pull the chart:</p>
                          <div className="bg-background p-2 rounded border font-mono text-xs overflow-x-auto">
                            helm pull oci://ghcr.io/kestrelai/charts/{tokenModalData.chartName} --version {tokenModalData.chartVersion}
                          </div>
                          <p className="text-muted-foreground">This downloads <code>{tokenModalData.chartName}-{tokenModalData.chartVersion}.tgz</code></p>
                        </div>

                        <div className="space-y-2">
                          <p><strong>Step 2: Pull and push the operator image</strong></p>
                          <p className="text-muted-foreground">Pull the operator image from GHCR:</p>
                          <div className="bg-background p-2 rounded border font-mono text-xs overflow-x-auto">
                            docker pull ghcr.io/kestrelai/{tokenModalData.chartName}:v1.0.0
                          </div>
                          <p className="text-muted-foreground">Tag and push to your private container registry:</p>
                          <div className="bg-background p-2 rounded border font-mono text-xs overflow-x-auto">
                            docker tag ghcr.io/kestrelai/{tokenModalData.chartName}:v1.0.0 &lt;your-registry&gt;/kestrel-operator:v1.0.0<br/>
                            docker push &lt;your-registry&gt;/kestrel-operator:v1.0.0
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p><strong>Step 3: Update values file</strong></p>
                          <p className="text-muted-foreground">Add this to your downloaded values file to use your private registry:</p>
                          <div className="bg-background p-2 rounded border font-mono text-xs overflow-x-auto">
                            image:<br/>
                            &nbsp;&nbsp;repository: &lt;your-registry&gt;/kestrel-operator<br/>
                            &nbsp;&nbsp;tag: v1.0.0
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p><strong>Step 4: Install from local chart</strong></p>
                          <p className="text-muted-foreground">Transfer the chart file to your air-gapped environment and install:</p>
                          <div className="bg-background p-2 rounded border font-mono text-xs overflow-x-auto">
                            helm install kestrel-operator ./{tokenModalData.chartName}-{tokenModalData.chartVersion}.tgz --namespace kestrel-ai --create-namespace -f kestrel-ai-operator-values-{tokenModalData.clusterName}.yaml
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Kestrel Operator Credential?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. The operator for cluster <strong>"{clusterToDelete?.name}"</strong> will lose access immediately and stop sending network policy data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setClusterToDelete(null)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDeleteToken}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete Credential
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Safe-Apply Confirmation Dialog */}
        <AlertDialog open={safeApplyDialogOpen} onOpenChange={setSafeApplyDialogOpen}>
          <AlertDialogContent className="max-w-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                {pendingSafeApplyChange?.safeApply ? 'Enable Safe-Apply' : 'Disable Safe-Apply'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                {pendingSafeApplyChange?.safeApply ? (
                  <>
                    You are about to enable Safe-Apply for cluster <strong>"{tokens.find(t => t.cluster_id === pendingSafeApplyChange?.clusterId)?.cluster_name}"</strong>.
                  </>
                ) : (
                  <>
                    You are about to disable Safe-Apply for cluster <strong>"{tokens.find(t => t.cluster_id === pendingSafeApplyChange?.clusterId)?.cluster_name}"</strong>.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            {pendingSafeApplyChange?.safeApply && (
              <div className="py-4 space-y-4">
                <Alert>
                  <AlertDescription className="font-medium">
                    This will allow APPROVED Kubernetes resources to be applied to your cluster from the AI chat interface.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Approval Required</p>
                      <p className="text-muted-foreground">
                        Resources can only be applied after proper approvals from namespace owners and security admins (if configured).
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Full Audit Trail</p>
                      <p className="text-muted-foreground">
                        All resource changes are logged in the Audit Log with details about who approved and applied them.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Layers className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Resource Operations</p>
                      <p className="text-muted-foreground">
                        Approved resources can be <strong>created</strong>, <strong>updated</strong>, or <strong>deleted</strong> directly from the AI chat interface.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> You can disable this setting at any time. Only resources with proper approvals will ever be applied to your cluster.
                  </p>
                </div>
              </div>
            )}
            
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setSafeApplyDialogOpen(false);
                setPendingSafeApplyChange(null);
              }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmSafeApplyChange}
                className={pendingSafeApplyChange?.safeApply 
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                }
              >
                {pendingSafeApplyChange?.safeApply ? 'Enable Safe-Apply' : 'Disable Safe-Apply'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Auto-Remediation Configuration Dialog */}
        <Dialog open={autoRemediationDialogOpen} onOpenChange={(open) => {
          setAutoRemediationDialogOpen(open);
          if (!open) {
            setEditingAutoRemediation(null);
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Configure Auto-Remediation
                <Badge variant="secondary" className="text-xs">BETA</Badge>
              </DialogTitle>
              <DialogDescription>
                {editingAutoRemediation?.clusterName && (
                  <span>Configure auto-remediation settings for <strong>{editingAutoRemediation.clusterName}</strong></span>
                )}
              </DialogDescription>
            </DialogHeader>

            {editingAutoRemediation && (
              <div className="space-y-4">
                {!editingAutoRemediation.safeApply && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Safe-apply must be enabled before you can configure auto-remediation. Please enable safe-apply first.
                    </AlertDescription>
                  </Alert>
                )}

                {editingAutoRemediation.safeApply && (
                  <>
                    <Alert className="bg-muted/50 border">
                      <AlertDescription className="text-sm">
                        <strong>Warning:</strong> When enabled, AI-generated fixes will be automatically applied to your cluster without manual approval for the selected incident types.
                      </AlertDescription>
                    </Alert>

                    <div className="flex items-center space-x-3 p-4 border rounded-lg">
                      <Switch
                        id="auto_remediate_edit"
                        checked={tempAutoRemediateSettings.enabled}
                        onCheckedChange={(checked) => {
                          setTempAutoRemediateSettings(prev => ({
                            enabled: checked,
                            incidentTypes: checked ? prev.incidentTypes : []
                          }));
                        }}
                      />
                      <div className="flex-1">
                        <FormLabel htmlFor="auto_remediate_edit" className="font-medium">
                          Enable Auto-Remediation
                        </FormLabel>
                        <p className="text-sm text-muted-foreground mt-1">
                          Automatically apply AI-generated fixes for selected incident types
                        </p>
                      </div>
                    </div>

                    {tempAutoRemediateSettings.enabled && (
                      <div>
                        <FormLabel className="text-sm font-medium mb-2 block">
                          Select Incident Types to Auto-Remediate
                        </FormLabel>
                        <p className="text-xs text-muted-foreground mb-3">
                          Only non-transient incidents of these types will be automatically fixed.
                        </p>
                        <div className="space-y-2 max-h-80 overflow-y-auto p-3 bg-muted/30 rounded-md border">
                          {INCIDENT_TYPES.map((incidentType) => (
                            <div key={incidentType.value} className="flex items-start space-x-3 p-2 hover:bg-muted rounded">
                              <input
                                type="checkbox"
                                id={`edit-incident-type-${incidentType.value}`}
                                checked={tempAutoRemediateSettings.incidentTypes.includes(incidentType.value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setTempAutoRemediateSettings(prev => ({
                                      ...prev,
                                      incidentTypes: [...prev.incidentTypes, incidentType.value]
                                    }));
                                  } else {
                                    setTempAutoRemediateSettings(prev => ({
                                      ...prev,
                                      incidentTypes: prev.incidentTypes.filter(t => t !== incidentType.value)
                                    }));
                                  }
                                }}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <label htmlFor={`edit-incident-type-${incidentType.value}`} className="flex-1 cursor-pointer">
                                <div className="font-medium text-sm">{incidentType.label}</div>
                                <div className="text-xs text-muted-foreground">{incidentType.description}</div>
                              </label>
                            </div>
                          ))}
                        </div>
                        {tempAutoRemediateSettings.incidentTypes.length === 0 && (
                          <Alert className="mt-3">
                            <AlertDescription className="text-xs">
                              No incident types selected. Auto-remediation will not trigger until you select at least one type.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setAutoRemediationDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={saveAutoRemediationSettings}
                        disabled={tempAutoRemediateSettings.enabled && tempAutoRemediateSettings.incidentTypes.length === 0}
                        className=""
                      >
                        Save Settings
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default GenerateOperatorToken; 